import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { validatePassword, calculatePasswordStrength, type PasswordPolicy } from "../utils/passwordValidation";
import { translateApiError } from "../i18n/apiError";

interface ResetPasswordProps {
  token: string; // Reset token from URL parameter
  onSuccess: () => void;
  onBack: () => void;
}

/**
 * ResetPassword Component
 *
 * Allows users to set a new password using a reset token from email.
 *
 * Expected API Endpoint (to be implemented):
 * POST /api/v1/auth/reset-password
 * Request: { token: string; new_password: string }
 * Response: { message: string }
 */
export const ResetPassword: React.FC<ResetPasswordProps> = ({
  token,
  onSuccess,
  onBack,
}) => {
  const { t } = useTranslation("resetPassword");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);

  // Fetch password policy from server on mount
  useEffect(() => {
    apiService.getPasswordPolicy()
      .then(policy => {
        setPasswordPolicy(policy);
        setPolicyLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch password policy:", err);
        // Fallback to production policy if fetch fails
        setPasswordPolicy({
          min_length: 12,
          require_uppercase: true,
          require_lowercase: true,
          require_digit: true,
          require_special_char: true,
          special_chars: "!@#$%^&*(),.?\":{}|<>_-+=[]\\/~`",
          forbidden_patterns: ["password", "admin", "qwerty", "letmein", "welcome", "123456"],
        });
        setPolicyLoading(false);
      });
  }, []);

  // Password validation using fetched policy
  useEffect(() => {
    if (!passwordPolicy) return;

    const errors: string[] = [];

    if (newPassword) {
      const policyErrors = validatePassword(newPassword, passwordPolicy);
      errors.push(...policyErrors);
    }

    if (confirmPassword && newPassword !== confirmPassword) {
      errors.push(t("errors.passwordMismatch"));
    }

    setValidationErrors(errors);
  }, [newPassword, confirmPassword, passwordPolicy]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validationErrors.length > 0) {
      return;
    }

    setError("");
    setLoading(true);

    try {
      await apiService.resetPassword(token, newPassword);
      onSuccess();
    } catch (err) {
      setError(translateApiError(t, err));
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordPolicy && newPassword
    ? calculatePasswordStrength(newPassword, passwordPolicy)
    : null;

  return (
    <div
      style={{
        maxWidth: "400px",
        margin: "50px auto",
        padding: "30px",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ marginBottom: "10px", textAlign: "center" }}>
        {t("title")}
      </h2>
      <p
        style={{
          textAlign: "center",
          color: "#6c757d",
          marginBottom: "30px",
          fontSize: "14px",
        }}
      >
        {t("subtitle")}
      </p>

      {error && (
        <div
          style={{
            padding: "12px",
            background: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            marginBottom: "20px",
            color: "#721c24",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            {t("form.newPasswordLabel")}
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            placeholder={t("form.newPasswordPlaceholder")}
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {strength && (
            <div
              style={{
                marginTop: "5px",
                fontSize: "12px",
                color: strength.color,
              }}
            >
              {t("form.strengthLabel")} <strong>{strength.label}</strong>
            </div>
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            {t("form.confirmPasswordLabel")}
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            placeholder={t("form.confirmPasswordPlaceholder")}
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        {validationErrors.length > 0 && (
          <div
            style={{
              padding: "12px",
              background: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                marginBottom: "8px",
                fontSize: "14px",
              }}
            >
              {t("form.requirementsTitle")}
            </div>
            <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "13px" }}>
              {validationErrors.map((err, idx) => (
                <li key={idx} style={{ color: "#856404" }}>
                  {err}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="submit"
          disabled={
            policyLoading ||
            loading ||
            validationErrors.length > 0 ||
            !newPassword ||
            !confirmPassword
          }
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor:
              policyLoading ||
              loading ||
              validationErrors.length > 0 ||
              !newPassword ||
              !confirmPassword
                ? "#6c757d"
                : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              policyLoading ||
              loading ||
              validationErrors.length > 0 ||
              !newPassword ||
              !confirmPassword
                ? "not-allowed"
                : "pointer",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          {policyLoading ? t("loading") : loading ? t("resetting") : t("submit")}
        </button>

        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "transparent",
            color: "#007bff",
            border: "none",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "14px",
            marginTop: "10px",
          }}
        >
          {t("backToLogin")}
        </button>
      </form>
    </div>
  );
};
